import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, Send, Flag } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { timeAgo } from "@/lib/categories";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { EmojiButton, EmojiPreview } from "@/components/EmojiButton";
import { EmojiText } from "@/lib/emoji";
import { markSeen } from "@/lib/seenPosts";

export default function CommunityThread() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, requireAuth } = useAuth();
  const [post, setPost] = useState(null);
  const [text, setText] = useState("");
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => { setFailed(false); api.get(`/community/posts/${id}`).then((r) => { setPost(r.data); markSeen({ id, comment_count: Math.max(r.data.comment_count || 0, (r.data.comments || []).length) }); }).catch(() => setFailed(true)); }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!post && failed) return (
    <div data-testid="thread-error" className="p-8 text-center">
      <p className="text-slate-500">Biseda nuk u hap. Kontrollo internetin.</p>
      <div className="flex justify-center gap-2 mt-4">
        <button data-testid="thread-retry" onClick={load} className="bg-cyan-500 text-white font-semibold rounded-full px-5 h-10">Provo përsëri</button>
        <button onClick={() => nav("/komunitete")} className="border border-slate-200 text-slate-600 font-semibold rounded-full px-5 h-10">Kthehu</button>
      </div>
    </div>
  );
  if (!post) return <div className="p-8 text-center text-slate-400">Duke ngarkuar...</div>;
  const liked = user && post.likes?.includes(user.id);

  const like = () => requireAuth(async () => { await api.post(`/community/posts/${id}/like`); load(); });
  const comment = () => requireAuth(async () => {
    if (!text.trim()) return;
    try { await api.post(`/community/posts/${id}/comments`, { body: text }); setText(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
  });
  const report = () => requireAuth(async () => { await api.post("/reports", { target_type: "post", target_id: id, reason: "përmbajtje e papërshtatshme" }); toast.success("Raportimi u dërgua"); });

  return (
    <div className="gs-fade pb-28">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-white sticky top-[57px] z-10">
        <button data-testid="thread-back" onClick={() => nav("/komunitete")}><ArrowLeft className="w-5 h-5" /></button>
        <span className="font-semibold text-slate-800">Biseda</span>
      </div>

      <div className="px-4 pt-4">
        <div className="flex items-center gap-2 text-xs mb-2">
          <span className="bg-cyan-50 text-cyan-600 font-semibold px-2 py-0.5 rounded-full">{post.topic}</span>
          <span className="text-slate-400">{post.city ? `${post.city}, ` : ""}{post.country}</span>
        </div>
        <h1 className="text-xl font-extrabold text-slate-900">{post.title}</h1>
        {post.body && <p className="text-slate-600 mt-2 whitespace-pre-line"><EmojiText body={post.body} /></p>}
        <div className="flex items-center gap-2 mt-3 text-sm text-slate-400">
          <div className="w-7 h-7 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center font-bold text-xs">{post.author_name?.[0]}</div>
          {post.author_name} · {timeAgo(post.created_at)}
        </div>
        <div className="flex items-center gap-3 mt-3">
          <button data-testid="like-post" onClick={like} className={`flex items-center gap-1 text-sm font-semibold px-3 py-1.5 rounded-full border ${liked ? "bg-red-50 text-red-500 border-red-200" : "border-slate-200 text-slate-600"}`}>
            <Heart className={`w-4 h-4 ${liked ? "fill-red-500" : ""}`} />{post.likes?.length || 0}
          </button>
          <button data-testid="report-post" onClick={report} className="flex items-center gap-1 text-sm text-slate-400"><Flag className="w-4 h-4" />Raporto</button>
        </div>
      </div>

      <div className="px-4 mt-5">
        <h2 className="font-bold text-slate-800 mb-2">Përgjigjet ({post.comments?.length || 0})</h2>
        <div className="space-y-2">
          {post.comments?.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl p-3 border border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs">{c.author_name?.[0]}</div>
                <span className="font-semibold text-sm text-slate-800">{c.author_name}</span>
                <span className="text-xs text-slate-400">{timeAgo(c.created_at)}</span>
              </div>
              <p className="text-sm text-slate-600 mt-1.5"><EmojiText body={c.body} /></p>
            </div>
          ))}
        </div>
      </div>

      {createPortal(<div className="fixed bottom-20 left-0 right-0 z-40 md:max-w-2xl md:mx-auto px-4">
        {/:gs\d+:/.test(text) && <div className="bg-white rounded-2xl border border-slate-100 shadow px-3 py-1 mb-2"><EmojiPreview text={text} testid="comment-emoji-preview" /></div>}
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-full p-1 shadow-lg">
          <EmojiButton testid="comment-emoji-btn" className="ml-1" onPick={(em) => setText((t) => t + em)} />
          <input data-testid="comment-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && comment()} placeholder="Shkruaj një përgjigje..." className="flex-1 px-3 h-10 outline-none text-sm bg-transparent" />
          <button data-testid="submit-comment" onClick={comment} className="bg-cyan-500 text-white rounded-full p-2.5"><Send className="w-4 h-4" /></button>
        </div>
      </div>, document.body)}
    </div>
  );
}
